import { useState } from 'react'
import { useReorderData } from '../hooks/useReorderData.js'
import { formatMoney, formatQty } from '../lib/format.js'
import OrderConfirmModal from './OrderConfirmModal.jsx'
import POEmailModal from './POEmailModal.jsx'
import SuggestReorderModal from './SuggestReorderModal.jsx'
import { Skeleton, SkeletonCard, SkeletonList } from './Skeleton.jsx'
import StatusPill from './StatusPill.jsx'
import { toneFor, labelFor } from '../lib/pillStatus.js'
import Toast from './Toast.jsx'
import KitchenLoader from './KitchenLoader.jsx'

// Reorder Suggestions screen (SOP 3).
// Layout:
//   1. Restock summary card — items below par + estimated cost
//   2. Per-supplier groups — items needing reorder, with a
//      "Send Order" button that opens OrderConfirmModal
//   3. Recent orders list
//
// On-hand is updated during the Send Order flow, not inline. That
// matches how a real kitchen does it: count stock for one supplier,
// place that supplier's order, move on.
export default function ReorderSuggestionsScreen() {
  const {
    items,
    needingReorder,
    groupedBySupplier,
    restockCost,
    recentOrders,
    restaurant,
    receiveLogs,
    wasteLogs,
    itemSales,
    pendingReorderAction,
    loading,
    sendOrder,
    markReceived,
    approveReorder,
    skipReorder,
  } = useReorderData()

  const [activeGroup, setActiveGroup] = useState(null)
  const [suggestOpen, setSuggestOpen] = useState(false)
  const [toast, setToast] = useState('')
  const [receivingId, setReceivingId] = useState(null)
  // Approve-and-execute in-flight state. 'approve' shows the KitchenLoader
  // (an execution state, never the blue spinner); 'skip' just disables.
  const [approveBusy, setApproveBusy] = useState(null)
  // Email modal state. Set when a fresh order is sent (chains from
  // OrderConfirmModal) OR when the manager taps "Email" on a past PO.
  const [emailPO, setEmailPO] = useState(null)
  const [emailLineItems, setEmailLineItems] = useState(null)

  async function handleMarkReceived(po) {
    if (receivingId) return
    const itemCount = po.items?.length ?? 0
    const ok = window.confirm(
      `Mark this delivery as received?\n\n${po.supplier_name} · ${itemCount} ${
        itemCount === 1 ? 'item' : 'items'
      } · ${formatMoney(po.total_cost)}\n\nThis will:\n• Log each line item as a delivery\n• Update on-hand quantities\n• Mark the order as received`
    )
    if (!ok) return
    setReceivingId(po.id)
    try {
      await markReceived(po.id)
      setToast(`Received ${formatMoney(po.total_cost)} from ${po.supplier_name}.`)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[handleMarkReceived]', e)
      setToast('Could not mark as received. Try again.')
    } finally {
      setReceivingId(null)
    }
  }

  // Approve the frozen reorder action — the agent's one-tap PO placement.
  // KitchenLoader shows while in flight (execution state). On failure we
  // keep the card mounted so the operator can retry; the fn is idempotent.
  async function handleApproveReorder() {
    if (approveBusy || !pendingReorderAction) return
    const supplierName = pendingReorderAction.spec?.supplier?.name ?? 'supplier'
    setApproveBusy('approve')
    try {
      await approveReorder(pendingReorderAction.id)
      setToast(`Order placed for ${supplierName}.`)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[handleApproveReorder]', e)
      setToast("We couldn't place the order. Try again.")
    } finally {
      setApproveBusy(null)
    }
  }

  async function handleSkipReorder() {
    if (approveBusy || !pendingReorderAction) return
    setApproveBusy('skip')
    try {
      await skipReorder(pendingReorderAction.id)
      setToast('Reorder skipped.')
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[handleSkipReorder]', e)
      setToast("We couldn't skip that. Try again.")
    } finally {
      setApproveBusy(null)
    }
  }

  async function handleConfirmOrder({ lineItems, notes }) {
    const supplierName = activeGroup?.supplierName ?? 'supplier'
    const po = await sendOrder({
      supplier: activeGroup?.supplier ?? null,
      lineItems,
      notes,
    })
    const total = lineItems.reduce(
      (s, li) => s + Number(li.total_cost || 0), 0
    )

    // Close OrderConfirmModal then chain into POEmailModal so the
    // manager can copy the email body to send to the supplier.
    setActiveGroup(null)
    setEmailPO(po)
    setEmailLineItems(lineItems)

    setToast(
      `Order sent to ${supplierName} · ${formatMoney(total)} · ${lineItems.length} ${
        lineItems.length === 1 ? 'item' : 'items'
      }. Copy the email below to send.`
    )
  }

  // AI Suggest path — receives one or more { supplier, lineItems, notes }
  // groups (one per supplier) and creates a PO for each via the existing
  // sendOrder mutator. Each result is reported via the toast.
  async function handleSuggestedOrders(orders) {
    let totalItems = 0, totalCost = 0
    for (const o of orders) {
      const po = await sendOrder({
        supplier:  o.supplier,
        lineItems: o.lineItems,
        notes:     o.notes,
      })
      totalItems += o.lineItems.length
      totalCost  += o.lineItems.reduce((s, li) => s + Number(li.total_cost || 0), 0)
      // Don't auto-open the email modal for AI batches — would be
      // disruptive across multiple suppliers. Manager can email from
      // the recent-orders list as needed.
      void po
    }
    setToast(
      `${orders.length} order${orders.length === 1 ? '' : 's'} created · ` +
      `${totalItems} ${totalItems === 1 ? 'line' : 'lines'} · ` +
      `${formatMoney(totalCost)}.`
    )
  }

  // "Email" button on Recent Orders — re-grab the email body for
  // an already-sent order. Uses the nested items from the order row
  // (which includes cost_per_unit_at_log for accurate per-unit display).
  function openEmailForExistingOrder(po) {
    setEmailPO(po)
    setEmailLineItems(po.items ?? [])
  }

  function closeEmailModal() {
    setEmailPO(null)
    setEmailLineItems(null)
  }

  const supplierCount = groupedBySupplier.length
  const itemCount = needingReorder.length

  // Friendly relative time for the recent orders list.
  const fmtTime = (iso) => {
    if (!iso) return ''
    const d = new Date(iso)
    const now = new Date()
    const isSameDay =
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate()
    if (isSameDay) {
      return d.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
      })
    }
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    })
  }

  // Short local clock time for the approve card's "Suggested at <time>" label,
  // derived from the pending action's created_at (the brief time). e.g.
  // "6:02 AM". Returns '' for empty/unparseable input so the label is skipped.
  const fmtBriefTime = (iso) => {
    if (!iso) return ''
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  }

  // First-paint skeleton — items + recent orders are fetching.
  if (loading && needingReorder.length === 0 && recentOrders.length === 0) {
    return (
      <div className="min-h-full bg-white pb-12">
        <main className="mx-auto max-w-screen-sm">
          <SkeletonCard />
          <section className="mx-4 mt-6">
            <Skeleton className="h-4 w-32" />
            <SkeletonList rows={4} className="mt-2" />
          </section>
          <section className="mx-4 mt-6">
            <Skeleton className="h-4 w-32" />
            <SkeletonList rows={3} className="mt-2" />
          </section>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-full bg-white pb-12">
      <main className="mx-auto max-w-screen-sm">
        {/* Approve-and-execute card — the morning brief's one-tap reorder.
            Shows the frozen spec summary; Approve places ONE purchase order
            for the supplier, Skip dismisses. Sentence case throughout. */}
        {pendingReorderAction && (
          <section
            className="mx-4 mt-4 rounded-2xl border border-amber-brand-400/40 bg-amber-50 p-5 shadow-sm"
            aria-label="Reorder awaiting approval"
          >
            <div className="text-xs font-medium text-amber-900/70">
              Ready to place from this morning's brief
            </div>
            <div className="mt-2 text-base font-medium text-black">
              {pendingReorderAction.spec?.supplier?.name ?? 'Supplier'}
            </div>
            {(() => {
              const asOf = fmtBriefTime(pendingReorderAction.created_at)
              return asOf ? (
                <div className="mt-0.5 text-xs text-black/55">Suggested at {asOf}</div>
              ) : null
            })()}

            {/* Line-item breakdown — exactly what one tap orders. Each row:
                item name, quantity × unit, cost per unit, and line total, read
                verbatim off the frozen spec so the card and the placed PO agree. */}
            <ul className="mt-3 overflow-hidden rounded-xl border border-amber-brand-400/30 bg-white">
              {(pendingReorderAction.spec?.line_items ?? []).map((li, idx) => (
                <li
                  key={li.item_id ?? `${li.item_name}-${idx}`}
                  className="flex items-baseline justify-between gap-3 border-b border-black/5 px-3 py-2 last:border-b-0"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm text-black truncate">{li.item_name}</div>
                    <div className="text-xs text-black/55">
                      {formatQty(Number(li.quantity), li.unit)} ·{' '}
                      <span className="tabular-nums">{formatMoney(li.cost_per_unit)}</span> each
                    </div>
                  </div>
                  <div className="text-right text-sm font-medium tabular-nums text-black">
                    {formatMoney(li.total_cost)}
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-2 flex items-baseline justify-between text-sm">
              <span className="text-black/70">
                {(() => {
                  const n = pendingReorderAction.spec?.line_items?.length ?? 0
                  return `${n} ${n === 1 ? 'item' : 'items'}`
                })()}
              </span>
              <span className="font-medium tabular-nums text-black">
                {formatMoney(pendingReorderAction.spec?.total_cost ?? 0)}
              </span>
            </div>

            {approveBusy === 'approve' ? (
              <div className="mt-4">
                <KitchenLoader
                  size="sm"
                  align="row"
                  messages={['Placing your order…', 'Writing it up…', 'Almost there…']}
                />
              </div>
            ) : (
              <div className="mt-4 flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleApproveReorder}
                  disabled={!!approveBusy}
                  className="min-h-btn flex-1 rounded-lg bg-emerald-600 px-4 py-3 text-base font-medium text-white hover:bg-emerald-700 active:bg-emerald-800 disabled:cursor-wait disabled:opacity-50"
                >
                  Approve and place order
                </button>
                <button
                  type="button"
                  onClick={handleSkipReorder}
                  disabled={!!approveBusy}
                  className="min-h-btn rounded-lg border border-black/15 bg-white px-4 py-3 text-base font-medium text-black/70 hover:bg-black/5 disabled:cursor-wait disabled:opacity-50"
                >
                  Skip
                </button>
              </div>
            )}
            <p className="mt-2 text-xs text-black/55 leading-snug">
              Places one purchase order for this supplier. You still send it to
              them the usual way.
            </p>
          </section>
        )}

        {/* Summary card */}
        <section
          className="mx-4 mt-4 rounded-2xl border border-black/10 bg-white p-5 shadow-sm"
          aria-label="Restock summary"
        >
          <div className="text-xs font-medium text-black/60">
            Items needing reorder
          </div>
          <div className="mt-2 flex items-baseline gap-3">
            <span className="text-5xl font-medium tabular-nums text-black">
              {itemCount}
            </span>
            <span className="text-sm text-black/60">
              {itemCount === 1 ? 'item' : 'items'}
              {supplierCount > 0 && (
                <> · {supplierCount} {supplierCount === 1 ? 'supplier' : 'suppliers'}</>
              )}
            </span>
          </div>
          <div className="mt-2 text-sm text-black/70">
            {itemCount === 0 ? (
              'Everything is at or above par. Check back tomorrow.'
            ) : (
              <>
                Estimated to fully restock:{' '}
                <span className="font-medium tabular-nums">
                  {formatMoney(restockCost)}
                </span>
              </>
            )}
          </div>

          {/* AI Suggest — velocity-aware reorder plan. Sits in the
              summary card so it's the first thing the manager sees. */}
          <button
            type="button"
            onClick={() => setSuggestOpen(true)}
            disabled={(items?.length ?? 0) === 0}
            className="mt-3 min-h-btn w-full rounded-lg border border-blue-600 bg-white px-4 py-3 text-base font-medium text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            ✨ AI Suggest order
          </button>
          <p className="mt-1 text-xs text-black/55 leading-snug">
            Reads 30 days of receives, wastes, and sales to rank what to
            reorder by urgency. Velocity-aware — flags items that will
            run out before your next delivery.
          </p>
        </section>

        {/* Per-supplier groups */}
        {groupedBySupplier.map((g) => (
          <section
            key={g.supplierId}
            className="mx-4 mt-6"
            aria-label={`${g.supplierName} order suggestions`}
          >
            <div className="mb-2 flex items-baseline justify-between gap-3">
              <h2 className="text-base font-medium">
                {g.supplierName}
                <span className="ml-2 text-xs font-normal text-black/55">
                  {g.items.length} {g.items.length === 1 ? 'item' : 'items'}
                </span>
              </h2>
              <button
                type="button"
                onClick={() => setActiveGroup(g)}
                className="min-h-btn rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                Send Order →
              </button>
            </div>

            <ul className="overflow-hidden rounded-xl border border-black/10">
              {g.items.map((i) => {
                const par     = Number(i.par_qty)
                const onHand  = Number(i.current_on_hand)
                const need    = Math.max(0, par - onHand)
                const lineCost = need * Number(i.cost_per_unit)
                const pctRemaining = par > 0 ? (onHand / par) * 100 : 100
                // Color the bar: red <25%, amber 25-66%, green >66%
                const tone =
                  pctRemaining < 25
                    ? 'bg-red-600'
                    : pctRemaining < 67
                      ? 'bg-amber-brand-400'
                      : 'bg-emerald-600'
                return (
                  <li
                    key={i.id}
                    className="border-b border-black/5 px-4 py-3 last:border-b-0"
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="font-medium truncate">{i.name}</span>
                      <span className="text-right text-sm font-medium tabular-nums">
                        {formatMoney(lineCost)}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center gap-3 text-xs text-black/60">
                      <span>
                        On hand{' '}
                        <span className="tabular-nums font-medium text-black/80">
                          {formatQty(onHand, i.unit)}
                        </span>{' '}
                        of {formatQty(par, i.unit)}
                      </span>
                      <span className="text-black/40">·</span>
                      <span>
                        Order{' '}
                        <span className="tabular-nums font-medium text-black/80">
                          {formatQty(need, i.unit)}
                        </span>
                      </span>
                    </div>
                    {/* On-hand visualization bar */}
                    <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-black/5">
                      <div
                        className={`h-full ${tone}`}
                        style={{ width: `${Math.max(2, Math.min(100, pctRemaining))}%` }}
                        aria-hidden="true"
                      />
                    </div>
                  </li>
                )
              })}
            </ul>
          </section>
        ))}

        {/* Recent orders */}
        <section className="mx-4 mt-8 mb-4" aria-label="Recent orders">
          <h2 className="mb-2 text-base font-medium">Recent orders</h2>
          {recentOrders.length === 0 ? (
            <div className="rounded-xl border border-dashed border-black/15 px-4 py-6 text-center text-sm text-black/60">
              No orders sent yet. Tap{' '}
              <span className="font-medium text-blue-600">Send Order</span>{' '}
              above to place your first one.
            </div>
          ) : (
            <ul className="overflow-hidden rounded-xl border border-black/10">
              {recentOrders.map((po) => {
                const isReceiving = receivingId === po.id
                return (
                  <li
                    key={po.id}
                    className={`flex items-start justify-between gap-3 border-b border-black/5 px-4 py-3 last:border-b-0 ${
                      isReceiving ? 'opacity-50' : ''
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">
                        {po.supplier_name}
                      </div>
                      <div className="text-xs text-black/60">
                        {(po.items?.length ?? 0)} {po.items?.length === 1 ? 'item' : 'items'} ·{' '}
                        {fmtTime(po.created_at)}
                        {po.notes ? ` · ${po.notes}` : ''}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-base font-medium tabular-nums">
                        {formatMoney(po.total_cost)}
                      </div>
                      <span className="inline-block mt-0.5">
                        <StatusPill tone={toneFor('invoice', po.status)}>
                          {labelFor('invoice', po.status)}
                        </StatusPill>
                      </span>
                      {/* Mark-received button — only on sent POs */}
                      {po.status === 'sent' && (
                        <button
                          type="button"
                          onClick={() => handleMarkReceived(po)}
                          disabled={isReceiving}
                          className="ml-2 mt-1 rounded-lg bg-emerald-600 px-2 py-1 text-xs font-medium text-white hover:bg-emerald-700 active:bg-emerald-800 disabled:cursor-wait disabled:opacity-50"
                        >
                          {isReceiving ? '…' : 'Mark received'}
                        </button>
                      )}
                      {/* Email button — for any order with line items.
                          Lets manager re-grab the formatted email body. */}
                      {po.status !== 'cancelled' && (po.items?.length ?? 0) > 0 && (
                        <button
                          type="button"
                          onClick={() => openEmailForExistingOrder(po)}
                          className="ml-2 mt-1 rounded-lg border border-blue-600 bg-white px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50"
                        >
                          Email
                        </button>
                      )}
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </section>
      </main>

      <OrderConfirmModal
        open={!!activeGroup}
        onClose={() => setActiveGroup(null)}
        onConfirm={handleConfirmOrder}
        group={activeGroup}
      />

      <POEmailModal
        open={!!emailPO}
        onClose={closeEmailModal}
        po={emailPO}
        lineItems={emailLineItems}
        restaurantName={restaurant?.name}
      />

      <SuggestReorderModal
        open={suggestOpen}
        onClose={() => setSuggestOpen(false)}
        items={items}
        receiveLogs={receiveLogs}
        wasteLogs={wasteLogs}
        itemSales={itemSales}
        onCreateOrders={handleSuggestedOrders}
      />

      <Toast message={toast} onDone={() => setToast('')} />
    </div>
  )
}
